import { useState } from "react";

import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { Spot, SpotKind } from "../../../api/types";
import { Button, Dialog, Pill } from "../../../ui";
import { useCommandId, useRefresh } from "../hooks";
import { locatorWords } from "../lib/locator";
import { COVERAGE_WORDS, SPOT_KIND_WORDS, spotKindWord } from "../lib/words";
import { ElementPicker } from "./ElementPicker";
import { FormError, Select, TextArea, TextInput } from "./fields";
import { ElementLink } from "./panels";

type Which = "correct" | "not_data" | "home" | "park" | null;

interface Props {
  spot: Spot;
  editable: boolean;
  selected?: boolean;
  onSelect?: () => void;
}

/** What a button of this spot is about, short enough for a label: the words read, or where on the page they are. */
export function spotSubject(spot: Pick<Spot, "text_read" | "locator">): string {
  const text = (spot.text_read ?? "").replace(/\s+/g, " ").trim();
  if (!text) return locatorWords(spot.locator);
  return text.length > 50 ? `${text.slice(0, 49).trimEnd()}…` : text;
}

/**
 * The unit as printed, when the words read do not already hold it. Never added to the end of a row with several values,
 * which would pin the unit of one of them on all (UI-08).
 */
export function unitNote(spot: Pick<Spot, "text_read" | "unit_printed">): string | null {
  const unit = spot.unit_printed?.trim();
  if (!unit) return null;
  return (spot.text_read ?? "").toLowerCase().includes(unit.toLowerCase()) ? null : unit;
}

/** One spot: what was read, where, how it fits, and what the engineer can do about it (8.3). */
export function SpotRow({ spot, editable, selected, onSelect }: Props) {
  const [open, setOpen] = useState<Which>(null);
  const scope = useApiCommand("set_spot_scope");
  const refresh = useRefresh();
  const close = () => setOpen(null);
  const coverage = spot.coverage_word ? COVERAGE_WORDS[spot.coverage_word] : null;
  const subject = spotSubject(spot);
  const unit = unitNote(spot);

  return (
    <li className="spot" data-spot-id={spot.id} data-selected={selected || undefined} data-out={!spot.in_scope || undefined}>
      <div className="spot__head">
        <button type="button" className="spot__kind" onClick={onSelect} aria-label={`Show this spot: ${spotKindWord(spot.kind)}, ${subject}`}>
          {spotKindWord(spot.kind)}
        </button>
        <span className="spot__where muted">{locatorWords(spot.locator)}</span>
        {coverage && <Pill>{coverage}</Pill>}
        {!spot.readable && <Pill>Not read clearly</Pill>}
        {!spot.in_scope && <Pill>Out of scope</Pill>}
        {spot.stale && <Pill>Out of date</Pill>}
        {spot.added_by_hand && <Pill>Added by hand</Pill>}
        {spot.parked && <Pill>Parked for another item</Pill>}
      </div>
      <p className="spot__text">
        {spot.text_read || <span className="muted">No text</span>}
      </p>
      {unit && <p className="spot__unit muted">Unit as printed: {unit}</p>}
      {spot.kind === "instruction_like_text" && <p className="muted">This looks like an instruction. It is treated as data only and the agent does not act on it.</p>}
      {spot.not_data_reason && <p className="muted">Not data: {spot.not_data_reason}</p>}
      {spot.hint && <p className="muted">Hint: {spot.hint}</p>}
      {spot.how_it_fits && <p className="muted">How it fits: {spot.how_it_fits}</p>}
      {spot.home?.element_id && (
        <p className="muted">
          Home: <ElementLink id={spot.home.element_id} />
        </p>
      )}
      {spot.item_names_seen.length > 0 && <p className="muted">Item names seen: {spot.item_names_seen.join(", ")}</p>}
      {editable && (
        <div className="spot__actions row">
          <Button variant="quiet" onClick={() => setOpen("correct")} aria-label={`Correct the reading: ${subject}`}>
            Correct the reading
          </Button>
          <Button
            variant="quiet"
            busy={scope.isPending}
            aria-label={`${spot.in_scope ? "Take out of scope" : "Bring into scope"}: ${subject}`}
            onClick={() =>
              scope.mutate(
                { id: spot.id, in_scope: !spot.in_scope },
                {
                  onSuccess: () => refresh("spots", "deliverable"),
                },
              )
            }
          >
            {spot.in_scope ? "Take out of scope" : "Bring into scope"}
          </Button>
          <Button variant="quiet" onClick={() => setOpen("not_data")} aria-label={`Not data: ${subject}`}>
            Not data
          </Button>
          <Button variant="quiet" onClick={() => setOpen("home")} aria-label={`Settle where it belongs: ${subject}`}>
            Settle where it belongs
          </Button>
          <Button variant="quiet" onClick={() => setOpen("park")} aria-label={`Belongs to another item: ${subject}`}>
            Belongs to another item
          </Button>
        </div>
      )}
      <FormError message={scope.error ? refusalText(scope.error) : null} />
      {open === "correct" && <CorrectDialog spot={spot} onClose={close} />}
      {open === "not_data" && <NotDataDialog spot={spot} onClose={close} />}
      {open === "home" && <HomeDialog spot={spot} onClose={close} />}
      {open === "park" && <ParkDialog spot={spot} onClose={close} />}
    </li>
  );
}

function CorrectDialog({ spot, onClose }: { spot: Spot; onClose: () => void }) {
  const [text, setText] = useState(spot.text_read);
  const [unit, setUnit] = useState(spot.unit_printed ?? "");
  const [problem, setProblem] = useState<string | null>(null);
  const correct = useApiCommand("correct_spot");
  const refresh = useRefresh();
  const submit = () => {
    if (!text.trim()) {
      setProblem("Write what the document says here.");
      return;
    }
    correct.mutate(
      { id: spot.id, text_read: text.trim(), unit_printed: unit.trim() || null },
      {
        onSuccess: () => {
          refresh("spots");
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title="Correct the reading"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={correct.isPending} onClick={submit}>
            Save the correction
          </Button>
        </>
      }
    >
      <p className="muted">Where: {locatorWords(spot.locator)}</p>
      <TextArea label="What the document says here" required rows={2} value={text} error={problem} onChange={setText} />
      <TextInput label="The unit as printed (optional)" value={unit} onChange={setUnit} />
      <FormError message={correct.error ? refusalText(correct.error) : null} />
    </Dialog>
  );
}

function NotDataDialog({ spot, onClose }: { spot: Spot; onClose: () => void }) {
  const [reason, setReason] = useState(spot.not_data_reason ?? "");
  const [problem, setProblem] = useState<string | null>(null);
  const mark = useApiCommand("set_spot_not_data");
  const refresh = useRefresh();
  const submit = () => {
    if (!reason.trim()) {
      setProblem("Say why this is not data.");
      return;
    }
    mark.mutate(
      { id: spot.id, reason: reason.trim() },
      {
        onSuccess: () => {
          refresh("spots", "deliverable");
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title="Mark as not data"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={mark.isPending} onClick={submit}>
            Mark as not data
          </Button>
        </>
      }
    >
      <p>A logo, a free remark or a position is not data for the ontology. The reason is kept.</p>
      <TextArea label="Why is it not data?" required rows={2} value={reason} error={problem} onChange={setReason} />
      <FormError message={mark.error ? refusalText(mark.error) : null} />
    </Dialog>
  );
}

function HomeDialog({ spot, onClose }: { spot: Spot; onClose: () => void }) {
  const [homeId, setHomeId] = useState(spot.home?.element_id ?? "");
  const [leave, setLeave] = useState(false);
  const [note, setNote] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const settle = useApiCommand("settle_spot_home");
  const ids = useCommandId();
  const refresh = useRefresh();
  const submit = () => {
    if (!leave && !homeId) {
      setProblem("Choose the element it belongs to, or say another discipline takes it.");
      return;
    }
    if (leave && !note.trim()) {
      setProblem("Say which discipline takes it, or why.");
      return;
    }
    settle.mutate(
      leave
        ? { id: spot.id, word: "left_to_other_discipline", note: note.trim(), command_id: ids.current() }
        : { id: spot.id, home: { element_id: homeId }, command_id: ids.current() },
      {
        onSuccess: () => {
          ids.renew();
          refresh("spots", "deliverable");
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title="Settle where it belongs"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={settle.isPending} onClick={submit}>
            Settle it
          </Button>
        </>
      }
    >
      <p>
        <strong>{spot.text_read}</strong>
      </p>
      {!leave && (
        <ElementPicker
          label="The element it belongs to"
          kinds={["data_field", "item", "component", "pick_list", "document_kind"]}
          value={homeId}
          error={problem}
          onChange={setHomeId}
        />
      )}
      <label className="field__checkline">
        <input type="checkbox" checked={leave} onChange={(event) => setLeave(event.target.checked)} />
        Another discipline takes it
      </label>
      {leave && <TextArea label="Which discipline, or why" required rows={2} value={note} error={problem} onChange={setNote} />}
      <FormError message={settle.error ? refusalText(settle.error) : null} />
    </Dialog>
  );
}

function ParkDialog({ spot, onClose }: { spot: Spot; onClose: () => void }) {
  const [name, setName] = useState(spot.item_names_seen[0] ?? "");
  const [problem, setProblem] = useState<string | null>(null);
  const park = useApiCommand("park_spot");
  const refresh = useRefresh();
  const submit = () => {
    if (!name.trim()) {
      setProblem("Write the name of the item it belongs to.");
      return;
    }
    park.mutate(
      { id: spot.id, item_name: name.trim() },
      {
        onSuccess: () => {
          refresh("spots", "deliverable", "item_backlog", "home");
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title="It belongs to another item"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={park.isPending} onClick={submit}>
            Park it
          </Button>
        </>
      }
    >
      <p>It is parked under that item&apos;s name, even if no such item exists yet. A later session on that item can take it without reading the document again.</p>
      <TextInput label="Name of the item" required value={name} error={problem} onChange={setName} />
      <FormError message={park.error ? refusalText(park.error) : null} />
    </Dialog>
  );
}

/** The dialog that adds a spot the engineer marked on a page. */
export function AddSpotDialog({
  deliverableId,
  page,
  region,
  onClose,
}: {
  deliverableId: string;
  page: number;
  region: [number, number, number, number];
  onClose: () => void;
}) {
  const [kind, setKind] = useState<SpotKind | "">("");
  const [text, setText] = useState("");
  const [unit, setUnit] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const add = useApiCommand("add_spot_by_hand");
  const ids = useCommandId();
  const refresh = useRefresh();
  const submit = () => {
    const found: Record<string, string> = {};
    if (!kind) found.kind = "Say what kind of spot it is.";
    if (!text.trim()) found.text = "Write what the document says in this area.";
    setErrors(found);
    if (Object.keys(found).length > 0 || !kind) return;
    add.mutate(
      {
        deliverable_id: deliverableId,
        kind,
        locator: { page, region },
        text_read: text.trim(),
        unit_printed: unit.trim() || null,
        command_id: ids.current(),
      },
      {
        onSuccess: () => {
          ids.renew();
          refresh("spots", "deliverable");
          onClose();
        },
      },
    );
  };
  return (
    <Dialog
      closeOnBackdrop={false}
      open
      onClose={onClose}
      title="Add a spot you marked"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={add.isPending} onClick={submit}>
            Add the spot
          </Button>
        </>
      }
    >
      <p className="muted">Page {page}, the area you marked.</p>
      <Select
        label="What kind of spot is it?"
        required
        value={kind}
        placeholder="Choose one"
        options={Object.entries(SPOT_KIND_WORDS).map(([value, label]) => ({ value, label }))}
        error={errors.kind}
        onChange={(value) => setKind(value as SpotKind | "")}
      />
      <TextArea label="What the document says here" required rows={2} value={text} error={errors.text} onChange={setText} />
      <TextInput label="The unit as printed (optional)" value={unit} onChange={setUnit} />
      <FormError message={add.error ? refusalText(add.error) : null} />
    </Dialog>
  );
}
